import type { Stop, StopDetails } from '#domain/Stop.ts';
import type { Tour, TourCapacity, TourDetails } from '#domain/Tour.ts';

import type {
    ApiBaseStop,
    ApiDetailedStop,
    ApiTour,
    ApiTourCapacity,
    ApiTourListEntry,
    ApiTourProblem,
    ApiWriteTour,
} from '../model/types.ts';
import { mapStop, mapStopDetails } from './stopAdapter.ts';

const mapTourProblem = (problem: ApiTourProblem | undefined) => {
    if (!problem) {
        return undefined;
    }
    return {
        id: problem.id,
        type: problem.type,
    };
};

const getStops = (tour: { stops?: ApiBaseStop[] }): ApiBaseStop[] => tour.stops ?? [];

const getFirstStop = (tour: { stops?: ApiBaseStop[] }): Stop | undefined => {
    const stops = getStops(tour);
    const firstStop = stops[0];
    return firstStop ? mapStop(firstStop) : undefined;
};

const getLastStop = (tour: { stops?: ApiBaseStop[] }): Stop | undefined => {
    const stops = getStops(tour);
    const lastStop = stops.at(-1);
    return lastStop ? mapStop(lastStop) : undefined;
};

const mapTourCapacity = (capacity: ApiTourCapacity | undefined): TourCapacity | undefined =>
    capacity
        ? {
              maxWeightKg: capacity.max_weight_kg,
              maxLoadingMeter: capacity.max_loading_meter,
              maxVolumeLiter: capacity.max_volume_liter,
              maxVolumeCubicMeter: capacity.max_volume_cubic_meter,
          }
        : undefined;

const mapTourCapacityToApi = (capacity: TourCapacity | undefined): ApiTourCapacity | undefined =>
    capacity
        ? {
              max_weight_kg: capacity.maxWeightKg,
              max_loading_meter: capacity.maxLoadingMeter,
              max_volume_liter: capacity.maxVolumeLiter,
              max_volume_cubic_meter: capacity.maxVolumeCubicMeter,
          }
        : undefined;

export const mapTour = (tour: ApiTour | ApiTourListEntry): Tour => ({
    id: tour.id,
    searchName: tour.search_name,
    driverId: tour.driver_id,
    truck: {
        id: tour.truck_id,
    },
    trailer: {
        id: tour.trailer_id,
    },
    status: tour.status,
    progress: {
        totalStopAmount: tour.progress.total_stop_amount,
        finishedStopAmount: tour.progress.finished_stop_amount,
    },
    capacity: mapTourCapacity(tour.capacity),
    firstStop: getFirstStop(tour),
    lastStop: getLastStop(tour),
    problem: mapTourProblem(tour.problem),
});

export const mapTours = (tours: ApiTourListEntry[]): Tour[] => tours.map(mapTour);

export const mapTourStops = (stops: ApiDetailedStop[]): StopDetails[] => stops.map(mapStopDetails);

export const mapTourDetails = (tour: ApiTour): TourDetails => ({
    ...mapTour(tour),
    stops: mapTourStops(tour.stops ?? []),
});

export const mapTourToWriteTour = (tour: {
    searchName: string;
    driverId?: string;
    truck: {
        id: string;
    };
    trailer: {
        id?: string;
    };
    capacity?: Tour['capacity'];
    stops: Array<{ orderId: string; type: 'load' | 'unload' | 'execute'; index: number }>;
}): ApiWriteTour => ({
    search_name: tour.searchName,
    driver_id: tour.driverId,
    truck_id: tour.truck.id,
    trailer_id: tour.trailer.id,
    capacity: mapTourCapacityToApi(tour.capacity),
    stops: tour.stops.map(stop => ({
        order_id: stop.orderId,
        type: stop.type,
        index: stop.index,
    })),
});
